import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { getOrganizationFeed } from 'api/services/organizations/feed';
import { ErrorResponse } from 'api/types/errors';
import { FeedDto, FeedItemDto } from 'api/dtos/associations/feed';
import { useMemo, useEffect, useRef, useState } from 'react';

export interface OrganizationFeedQueryKey {
  organizationId: string;
  searchTerm?: string;
  limit?: number;
}

export const organizationFeedKeys = {
  all: ['organizationFeed'] as const,
  lists: () => [...organizationFeedKeys.all, 'list'] as const,
  list: (params: OrganizationFeedQueryKey) =>
    [...organizationFeedKeys.lists(), params] as const,
  infinite: (params: OrganizationFeedQueryKey) =>
    [...organizationFeedKeys.list(params), 'infinite'] as const
};

export const useOrganizationFeedInfinite = (
  params: {
    organizationId: string;
    searchTerm?: string;
    limit?: number;
    enabled?: boolean;
    useUnauthenticatedEndpoint?: boolean;
  },
  options?: Omit<
    UseInfiniteQueryOptions<FeedDto, ErrorResponse>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const {
    enabled = true,
    useUnauthenticatedEndpoint = false,
    limit = 20,
    ...queryParams
  } = params;

  const queryKey = organizationFeedKeys.infinite({
    organizationId: params.organizationId,
    searchTerm: params.searchTerm,
    limit: params.limit
  });

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage: rqFetchNextPage,
    refetch,
    error
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      return getOrganizationFeed({
        cursor: pageParam,
        limit,
        organizationId: queryParams.organizationId,
        searchTerm: queryParams.searchTerm,
        useUnauthenticatedEndpoint
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: enabled && !!params.organizationId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });

  const allItems = useMemo<FeedItemDto[]>(
    () =>
      isLoading || !data
        ? []
        : data.pages.flatMap((page) => page.items || []),
    [isLoading, data]
  );

  const lastPage = data?.pages?.[data.pages.length - 1];
  const hasNextPage = !!lastPage?.nextCursor;

  const fetchNextPage = () => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    if (lastPage?.nextCursor) {
      rqFetchNextPage();
    }
  };

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const fetchCountRef = useRef(0);

  useEffect(() => {
    if (!enabled || !params.organizationId) {
      return;
    }

    if (isLoading) {
      return;
    }

    const targetItemCount = 20;
    const shouldFetchMore = allItems.length < targetItemCount && hasNextPage && !isFetchingNextPage;

    if (shouldFetchMore && fetchCountRef.current < 10) {
      fetchCountRef.current += 1;
      rqFetchNextPage();
      return;
    }

    if (!isFetchingNextPage && !isLoading && (allItems.length >= targetItemCount || !hasNextPage)) {
      setIsInitialLoadComplete(true);
    }
  }, [enabled, params.organizationId, isLoading, isFetchingNextPage, allItems.length, hasNextPage, rqFetchNextPage]);

  const effectiveIsLoading = isLoading || !isInitialLoadComplete;

  return {
    data: allItems,
    isLoading: effectiveIsLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
    error
  };
};

